import React, { useEffect, useRef, useState } from "react";

function VisaWorld() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRefs = useRef([]);
  const contentRef = useRef(null);

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 5);

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = sectionRefs.current.indexOf(entry.target);
          setActiveIndex(index);
        }
      });
    }, observerOptions);

    sectionRefs.current.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToSection = (index) => {
    setActiveIndex(index);
    const section = sectionRefs.current[index];
    const contentElement = contentRef.current;

    const scrollPosition = section.offsetTop - contentElement.offsetTop;

    contentElement.scrollTo({
      top: scrollPosition,
      behavior: "smooth",
    });
  };

  return (
    <div className="flex flex-wrap">
      {/* Left-side Navigation */}
      <div className="sticky top-0 h-screen w-1/4 bg-gray-100 p-4 hidden md:block">
        <ul className="space-y-4">
          <li
            onClick={() => scrollToSection(0)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 0
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Benefits of the Danish Green Card
          </li>
          <li
            onClick={() => scrollToSection(1)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 1
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Steps to Apply for the Green Card
          </li>
          <li
            onClick={() => scrollToSection(2)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 2
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Mandatory Documents
          </li>
          <li
            onClick={() => scrollToSection(3)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 3
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Tips for Success
          </li>
          <li
            onClick={() => scrollToSection(4)}
            className={`cursor-pointer p-4 rounded-md shadow-md ${
              activeIndex === 4
                ? "bg-blue-500 text-white"
                : "bg-white text-[#2C3F69]"
            }`}
          >
            Why Choose Visa World?
          </li>
        </ul>
      </div>

      {/* Content Section */}
      <div
        ref={contentRef}
        className="mb-10 h-screen overflow-y-auto p-4 w-full md:w-3/4"
      >
        <div ref={(el) => (sectionRefs.current[0] = el)}>
          <p className="text-xl font-bold text-[#011473]">
            Benefits of the Denmark Danish Green Card
          </p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>Work Freedom: No need for a separate work permit to work in Denmark.</li>
            <li>Family Reunification: Bring your family to Denmark under the Green Card scheme.</li>
            <li>Access to Social Benefits: Enjoy healthcare, education and social security in Denmark.</li>
            <li>Pathway to Citizenship: Opportunity to secure permanent residency and Danish citizenship.</li>
          </ul>
        </div>

        <div ref={(el) => (sectionRefs.current[1] = el)} className="mt-10">
          <p className="text-lg font-semibold text-[#011473]">
            Steps to Apply for the Danish Green Card
          </p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>Check Eligibility: Verify if you meet the necessary requirements for the Green Card.</li>
            <li>Prepare Documents: Gather educational certificates, language test results and work experience proofs.</li>
            <li>Apply Online: Submit your application through the official Danish Immigration Service portal.</li>
            <li>Pay the Fee: Complete the payment for the application fee.</li>
            <li>Interview Process: Attend an embassy or consulate interview if required.</li>
            <li>Processing Time: Typically takes 6-12 months to process the application.</li>
          </ul>
        </div>

        <div ref={(el) => (sectionRefs.current[2] = el)} className="mt-10">
          <p className="text-lg font-semibold text-[#011473]">
            Mandatory Documents
          </p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>Valid passport</li>
            <li>Academic certificates and transcripts</li>
            <li>Language proficiency test results</li>
            <li>Proof of work experience</li>
            <li>Evidence of financial stability</li>
            <li>Health insurance details</li>
            <li>Completed application form</li>
            <li>Payment receipt for the fee</li>
          </ul>
        </div>

        <div ref={(el) => (sectionRefs.current[3] = el)} className="mt-10">
          <p className="text-lg font-semibold text-[#011473]">
            Tips for a Successful Application
          </p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>Provide Accurate Information: Ensure all details are correct and consistent.</li>
            <li>Organize Documents: Submit well-structured and complete paperwork.</li>
            <li>Emphasize Skills: Highlight your qualifications and expertise.</li>
            <li>Consider Professional Help: Seek expert advice for a smoother process.</li>
          </ul>
        </div>

        <div ref={(el) => (sectionRefs.current[4] = el)} className="mt-10">
          <p className="text-lg font-semibold text-[#011473]">
            Why Choose Visa World?
          </p>
          <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
            <li>Tailored Guidance: Personalized advice to suit your circumstances.</li>
            <li>Complete Documentation Support: Ensure you meet all requirements.</li>
            <li>Expert Application Review: Reduce errors and enhance approval chances.</li>
          </ul>
          <p className="mt-6 text-[#2C3F69]">
            Start your Danish Green Card journey with Visa World! Our experienced consultants will assist you in achieving your dreams of living and working in Denmark.
          </p>
        </div>
      </div>
    </div>
  );
}

export default VisaWorld;
